import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { takeActionOnDevices, UserKeys } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { MobileDevice } from '../../../../../types';
import SharedDialog from '../../../../shared/shared-dialog';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  ToolsRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { isFeatureEnabled } from '../../../../utils/feature-flags';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface WipeAccountUserDeviceDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail?: string;
  selectedDevices: MobileDevice[];
  changeLogRoles: string[];
  setSelectedDeviceIds: Dispatch<SetStateAction<string[]>>;
}

export default function WipeAccountUserDeviceDialog({
  open,
  handleClose,
  userEmail,
  selectedDevices,
  changeLogRoles,
  setSelectedDeviceIds,
}: WipeAccountUserDeviceDialogProps) {
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const { createEvent } = useAnalytics();
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const { features } = useUserSettings();

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      takeActionOnDevices('WIPE_ACCOUNT', selectedDevices, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message:
          selectedDevices.length > 1
            ? 'Devices account wiped successfully!'
            : 'Device account wiped successfully!',
      });

      if (isFeatureEnabled('indexMobileDevicesReact', features)) {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.MobileDevicesIndexedPage],
        });
      } else if (isFeatureEnabled('showMobileManagementFilters', features)) {
        queryClient.setQueryData(
          [UserKeys.SearchedMobileDevices],
          (oldData: MobileDevice[]) => {
            if (oldData) {
              const deviceIds = selectedDevices.map(
                (device) => device.resourceId
              );

              return oldData.map((device) => {
                if (deviceIds.includes(device.resourceId)) {
                  device.status = 'UNKNOWN';
                }
                return device;
              });
            }
          }
        );
      } else {
        queryClient.setQueryData(
          [UserKeys.Devices, userEmail],
          (oldData: MobileDevice[]) => {
            if (oldData) {
              const deviceIds = selectedDevices.map(
                (device) => device.resourceId
              );

              return oldData.map((device) => {
                if (deviceIds.includes(device.resourceId)) {
                  device.status = 'UNKNOWN';
                }
                return device;
              });
            }
          }
        );
      }

      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          selectedDevices.length > 1
            ? `There was an issue wiping these devices' accounts. Please refresh and try again.`
            : 'There was an issue wiping this devices account. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        userEmail
          ? analytics.user_management.user_details.devices
              .account_wipe_mobile_device
          : analytics.devices.mobile_management.account_wipe_mobile_device,
        userEmail
          ? {
              module: ModuleList.modules.userManagement.name,
              section: ModuleList.modules.userManagement.sections.mobileDevices,
            }
          : {
              module: ModuleList.modules.devices.name,
              section: ModuleList.modules.devices.sections.mobileManagement,
            }
      );
      setSelectedDeviceIds([]);
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        ToolsRoles.CanRemoteWipeAccountMobile.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }
  if (!selectedDevices?.length) return null;

  const isBulk = selectedDevices.length > 1;

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={
        isBulk ? 'Remote Account Wipe Devices' : 'Remote Account Wipe Device'
      }
      dialogContent={
        <Box>
          <Typography>
            {isBulk ? (
              <>
                Wipe accounts from{' '}
                <strong>{selectedDevices.length} selected devices</strong>?
              </>
            ) : (
              <>
                Wipe{' '}
                <strong>
                  {selectedDevices[0]?.email?.[0] || 'Unknown User'}
                </strong>
                's account from device{' '}
                <strong>{selectedDevices[0]?.model || 'Unknown Device'}</strong>
                ?
              </>
            )}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: isBulk ? 'Wipe Accounts' : 'Wipe Account',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'warning',
      }}
    />
  );
}
